import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';

const EDGE_PX = 88;
const MAX_PIXEL_SCROLL = 28;

const nativeScrollLeft =
    Object.getOwnPropertyDescriptor(Element.prototype, 'scrollLeft')
    || Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'scrollLeft');

function getTaskLists(): HTMLElement[] {
    return Array.from(document.querySelectorAll<HTMLElement>('.tasks-list'));
}

/**
 * Пока тянем карточку, JS-чтение list.scrollLeft отдаёт горизонтальный скролл доски.
 * hello-pangea/dnd не умеет вложенный скролл (колонка Y + доска X) и иначе
 * не обновляет координаты колонок при auto-scroll доски.
 */
function patchTaskListsScrollX(board: HTMLElement): () => void {
    if (!nativeScrollLeft?.get || !nativeScrollLeft.set) {
        return () => undefined;
    }

    const lists = getTaskLists();
    lists.forEach((list) => {
        Object.defineProperty(list, 'scrollLeft', {
            configurable: true,
            enumerable: true,
            get() {
                return board.scrollLeft;
            },
            set(value: number) {
                nativeScrollLeft.set!.call(this, value);
            },
        });
    });

    return () => {
        lists.forEach((list) => {
            delete (list as { scrollLeft?: number }).scrollLeft;
        });
    };
}

function publishListScroll() {
    getTaskLists().forEach((list) => {
        list.dispatchEvent(new Event('scroll'));
    });
}

/**
 * Горизонтальный auto-scroll доски при переносе карточки к краю
 * и синхронизация этого скролла с @hello-pangea/dnd.
 */
export function useTaskBoardAutoScroll<T extends HTMLElement>(
    boardScrollRef: RefObject<T | null>,
) {
    const draggingTaskRef = useRef(false);
    const pointerXRef = useRef(0);
    const pointerYRef = useRef(0);
    const dragStartAtRef = useRef(0);
    const rafRef = useRef<number>(0);
    const unpatchRef = useRef<(() => void) | null>(null);

    const stopRaf = () => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = 0;
        }
    };

    const tick = useCallback(() => {
        rafRef.current = 0;
        if (!draggingTaskRef.current) {
            return;
        }

        const board = boardScrollRef.current;
        if (!board) {
            return;
        }

        const rect = board.getBoundingClientRect();
        const x = pointerXRef.current;
        const y = pointerYRef.current;
        const inBoardY = y >= rect.top - 24 && y <= rect.bottom + 24;
        let dx = 0;

        if (inBoardY && x > rect.right - EDGE_PX) {
            const t = Math.min(1, Math.max(0, (x - (rect.right - EDGE_PX)) / EDGE_PX));
            dx = Math.ceil(MAX_PIXEL_SCROLL * t * t);
        } else if (inBoardY && x < rect.left + EDGE_PX) {
            const t = Math.min(1, Math.max(0, (rect.left + EDGE_PX - x) / EDGE_PX));
            dx = -Math.ceil(MAX_PIXEL_SCROLL * t * t);
        }

        const elapsed = Date.now() - dragStartAtRef.current;
        if (elapsed < 150) {
            dx = 0;
        } else if (elapsed < 400 && dx !== 0) {
            dx = Math.trunc(dx * ((elapsed - 150) / 250));
        }

        if (dx) {
            const max = board.scrollWidth - board.clientWidth;
            const next = Math.max(0, Math.min(max, board.scrollLeft + dx));
            if (next !== board.scrollLeft) {
                board.scrollLeft = next;
                publishListScroll();
            }
        }

        rafRef.current = requestAnimationFrame(tick);
    }, [boardScrollRef]);

    const stop = useCallback(() => {
        draggingTaskRef.current = false;
        stopRaf();
        unpatchRef.current?.();
        unpatchRef.current = null;
    }, []);

    const start = useCallback(() => {
        const board = boardScrollRef.current;
        if (!board) {
            return;
        }

        stop();
        draggingTaskRef.current = true;
        dragStartAtRef.current = Date.now();
        unpatchRef.current = patchTaskListsScrollX(board);
        rafRef.current = requestAnimationFrame(tick);
    }, [boardScrollRef, stop, tick]);

    useEffect(() => {
        const onPointerMove = (event: PointerEvent) => {
            pointerXRef.current = event.clientX;
            pointerYRef.current = event.clientY;
        };

        const onBoardScroll = () => {
            if (!draggingTaskRef.current) {
                return;
            }
            publishListScroll();
        };

        window.addEventListener('pointermove', onPointerMove, { passive: true });
        const board = boardScrollRef.current;
        board?.addEventListener('scroll', onBoardScroll, { passive: true });

        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            board?.removeEventListener('scroll', onBoardScroll);
            stop();
        };
    }, [boardScrollRef, stop]);

    return { start, stop };
}

export default useTaskBoardAutoScroll;
